// Nómina: corridas mensuales de planilla.
//
// Una corrida (payroll_run) es la foto de un mes: quién cobra qué, con el
// desglose congelado en el momento de cerrarla. Mientras está en borrador se
// recalcula desde la asistencia; al cerrarla los números dejan de moverse
// aunque después se editen marcaciones, que es lo que hace que una planilla
// pagada sea auditable.
import {
    collection, doc, addDoc, updateDoc, deleteDoc, onSnapshot,
    query, where, serverTimestamp, getDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import { getUserWorkspace } from './firestore';

const COLLECTION = 'payroll_runs';

let _wsId = null;
export const setPayrollWorkspace = async (userId) => {
    if (!userId) return null;
    _wsId = await getUserWorkspace(userId);
    return _wsId;
};
const withWs = (data) => ({ ...data, workspaceId: _wsId || 'default' });

export const subscribeToPayrollRuns = (callback) => {
    const colRef = collection(db, COLLECTION);
    // Se ordena en el cliente a proposito: filtrar por workspaceId y ordenar por
    // period a la vez exigiria un indice compuesto en Firestore, y son pocas
    // corridas (una por mes).
    const q = _wsId ? query(colRef, where('workspaceId', '==', _wsId)) : query(colRef);
    return onSnapshot(q, (snap) => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (b.period || '').localeCompare(a.period || ''));
        callback(list);
    }, (error) => {
        console.error('Error al leer las corridas de nómina:', error);
        callback([]);
    });
};

// Crea la corrida del mes con el desglose calculado. Si ya existe una en
// borrador para ese periodo, la reemplaza.
export const savePayrollRun = async ({ id, period, items, totals, notes, createdBy }) => {
    const payload = withWs({
        period,
        items,
        totals,
        notes: notes || '',
        regimen: 'interna', // preparado para 'ley' o 'rxh' más adelante
        updatedAt: serverTimestamp(),
    });
    // Al actualizar no se toca `status`: si no, guardar volvería a poner en
    // borrador una planilla ya cerrada.
    if (id) {
        await updateDoc(doc(db, COLLECTION, id), payload);
        return id;
    }
    const created = await addDoc(collection(db, COLLECTION), {
        ...payload,
        status: 'borrador',
        createdBy: createdBy || null,
        createdAt: serverTimestamp(),
    });
    return created.id;
};

export const closePayrollRun = async (runId, closedBy) => {
    await updateDoc(doc(db, COLLECTION, runId), {
        status: 'cerrado',
        closedAt: serverTimestamp(),
        closedBy: closedBy || null,
        updatedAt: serverTimestamp(),
    });
};

export const reopenPayrollRun = async (runId) => {
    await updateDoc(doc(db, COLLECTION, runId), {
        status: 'borrador',
        closedAt: null,
        updatedAt: serverTimestamp(),
    });
};

export const deletePayrollRun = async (runId) => {
    await deleteDoc(doc(db, COLLECTION, runId));
};

// Marca pagado a un colaborador dentro de la corrida y adjunta el comprobante.
// Se reescribe el array completo porque Firestore no sabe actualizar un
// elemento por índice sin leerlo antes.
export const markItemPaid = async (runId, userId, { file, paidBy, reference } = {}) => {
    const runRef = doc(db, COLLECTION, runId);
    const snap = await getDoc(runRef);
    if (!snap.exists()) throw new Error('La corrida de nómina no existe');

    let proofUrl = null;
    let proofName = null;
    if (file) {
        const ext = (file.name.split('.').pop() || 'dat').toLowerCase();
        const r = ref(storage, `payment_proofs/${userId}/nomina-${runId}-${Date.now()}.${ext}`);
        await uploadBytes(r, file);
        proofUrl = await getDownloadURL(r);
        proofName = file.name;
    }

    const items = (snap.data().items || []).map(it => it.userId === userId ? {
        ...it,
        paid: true,
        paidAt: new Date().toISOString(),
        paidBy: paidBy || null,
        reference: reference || '',
        ...(proofUrl ? { proofUrl, proofName } : {}),
    } : it);

    await updateDoc(runRef, { items, updatedAt: serverTimestamp() });
};

export const unmarkItemPaid = async (runId, userId) => {
    const runRef = doc(db, COLLECTION, runId);
    const snap = await getDoc(runRef);
    if (!snap.exists()) return;
    const items = (snap.data().items || []).map(it => it.userId === userId
        ? { ...it, paid: false, paidAt: null, paidBy: null }
        : it);
    await updateDoc(runRef, { items, updatedAt: serverTimestamp() });
};
